import React from "react"

import { ChoiceCard } from "@coval/ui/choice-card"

import { cn } from "@/utils/cn"

import {
  DISTRIBUTION_STAT_KEY,
  type DistributionStatKey,
  type DistributionStats,
} from "./distributionStatsUtils"

interface HistogramDistributionStatsProps {
  stats: DistributionStats
  className?: string
  formatValue: (v: number) => string
  units?: string
  /** When set, stats are clickable to toggle reference lines on the chart. */
  visibleStats?: Set<DistributionStatKey>
  onToggleStat?: (key: DistributionStatKey) => void
  /** When set, hovering a stat shows its reference line on the chart. */
  onHoverStat?: (key: DistributionStatKey | null) => void
}

const DISTRIBUTION_COLUMNS: Array<{
  key: DistributionStatKey
  label: string
}> = [
  { key: DISTRIBUTION_STAT_KEY.MEAN, label: "Mean" },
  { key: DISTRIBUTION_STAT_KEY.STD_DEV, label: "Standard Deviation" },
  { key: DISTRIBUTION_STAT_KEY.MEDIAN, label: "Median" },
  { key: DISTRIBUTION_STAT_KEY.P95, label: "P95" },
]

/**
 * Displays distribution stats in 4 columns (name + number in h1)
 * for histogram metric detail charts. When onToggleStat is provided,
 * clicking a stat toggles its reference line on the chart.
 */
export const HistogramDistributionStats: React.FC<
  HistogramDistributionStatsProps
> = ({
  stats,
  className,
  formatValue,
  units,
  visibleStats,
  onToggleStat,
  onHoverStat,
}) => {
  const unitSuffix = units ? ` ${units}` : ""
  const isInteractive = onToggleStat != null

  return (
    <div
      className={cn("grid grid-cols-4 gap-4 text-coval-text-muted", className)}
    >
      {DISTRIBUTION_COLUMNS.map(({ key, label }) => {
        const isActive = visibleStats?.has(key) ?? false
        const content = (
          <>
            <span className="text-sm font-medium text-coval-text">{label}</span>
            <p className="text-2xl font-bold text-coval-text mt-0.5">
              {formatValue(stats[key])}
              {unitSuffix}
            </p>
          </>
        )
        if (isInteractive) {
          return (
            <ChoiceCard
              key={key}
              title={label}
              aria-label={`Toggle ${label} reference line`}
              selected={isActive}
              onSelect={() => onToggleStat(key)}
              onMouseEnter={() => onHoverStat?.(key)}
              onMouseLeave={() => onHoverStat?.(null)}
              className="gap-0.5 px-3 py-2"
            >
              <p className="text-2xl font-bold text-coval-text">
                {formatValue(stats[key])}
                {unitSuffix}
              </p>
            </ChoiceCard>
          )
        }
        return (
          <div
            key={key}
            className="flex flex-col rounded px-3 py-2 border border-coval-border bg-surface-default"
          >
            {content}
          </div>
        )
      })}
    </div>
  )
}
